import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { PRESET_LAYOUTS } from '../utils/constants';

const initialCategories = {
  presets: {
    title: 'Preset Layouts',
    icon: '✨',
    expanded: true,
    blocks: Object.entries(PRESET_LAYOUTS).map(([id, preset]) => ({
      id: `preset-${id}`,
      name: preset.name,
      icon: preset.icon,
      keywords: preset.keywords || [],
      description: preset.description
    }))
  },
  basic: {
    title: 'Basic Blocks',
    icon: '📝',
    expanded: false,
    blocks: [
      { id: 'text', name: 'Text Block', icon: '📄', keywords: ['text', 'paragraph'] },
      { id: 'headline', name: 'Headline', icon: '📰', keywords: ['title', 'h1', 'heading'] },
      { id: 'list', name: 'List', icon: '📋', keywords: ['list', 'bullet', 'numbered'] },
      { id: 'apa-quote', name: 'APA Quote', icon: '❝', keywords: ['apa', 'quote', 'citation', 'reference'] },
      { id: 'apa-citation', name: 'Citation', icon: '📚', keywords: ['citation', 'apa', 'reference', 'source'] },
      { id: 'apa-reference-list', name: 'Reference List', icon: '📖', keywords: ['reference', 'references', 'apa', 'bibliography'] },
      { id: 'divider', name: 'Divider', icon: '➖', keywords: ['divider', 'line', 'separator'] },
      { id: 'html', name: 'Code Block', icon: '📑', keywords: ['code', 'html', 'programming'] },
      { id: 'link', name: 'Link Block', icon: '🔗', keywords: ['link', 'url', 'resource', 'website'] },
    ],
  },
  media: {
    title: 'Media',
    icon: '🎬',
    expanded: false,
    blocks: [
      { id: 'image', name: 'Image', icon: '🖼️', keywords: ['photo', 'picture', 'img'] },
      { id: 'video', name: 'Video', icon: '🎥', keywords: ['video', 'youtube', 'vimeo'] },
      { id: 'youtube-playlist', name: 'YouTube Playlist', icon: '📺', keywords: ['playlist', 'youtube', 'series', 'videos', 'watch list'] },
      { id: 'audio', name: 'Audio', icon: '🔊', keywords: ['audio', 'sound', 'podcast'] },
    ],
  },
  interactive: {
    title: 'Interactive & Cards',
    icon: '🎯',
    expanded: false,
    blocks: [
      { id: 'cards', name: 'Cards', icon: '🃏', keywords: ['cards', 'grid'] },
      { id: 'stacked-cards', name: 'Stacked Cards', icon: '🗂️', keywords: ['stack', 'timeline', 'cards'] },
      { id: 'multi-column-cards', name: 'Multi-Column Cards', icon: '📊', keywords: ['columns', 'multi', 'cards', 'checklist', 'milestones'] },
      { id: 'project-timeline', name: 'Project Timeline', icon: '📅', keywords: ['timeline', 'roadmap', 'milestones', 'project', 'phases', 'steps', 'schedule'] },
      { id: 'accordion', name: 'Accordion', icon: '📂', keywords: ['collapse', 'fold', 'expand'] },
      { id: 'info-box', name: 'Info Box', icon: 'ℹ️', keywords: ['info', 'note'] },
      { id: 'exercise-box', name: 'Exercise Box', icon: '🎯', keywords: ['exercise', 'practice'] },
      { id: 'warning-box', name: 'Warning Box', icon: '⚠️', keywords: ['warning', 'alert', 'caution'] },
    ],
  },
  advanced: {
    title: 'Advanced',
    icon: '🛠️',
    expanded: false,
    blocks: [
      { id: 'worksheet', name: 'Worksheet', icon: '📝', keywords: ['worksheet', 'quiz', 'questions'] },
    ],
  },
};

const BlockMenuOverlay = ({ isOpen, onClose, onSelect }) => {
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState(initialCategories);
  const menuRef = useRef(null);

  useEffect(() => {
    if (isOpen && menuRef.current) {
      const height = menuRef.current.offsetHeight;
      document.documentElement.style.setProperty('--add-menu-height', `${height + 16}px`);
      document.body.classList.add('add-block-menu-open');
    } else {
      document.body.classList.remove('add-block-menu-open');
      document.documentElement.style.removeProperty('--add-menu-height');
    }
    return () => {
      document.body.classList.remove('add-block-menu-open');
      document.documentElement.style.removeProperty('--add-menu-height');
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    const result = {};
    Object.entries(categories).forEach(([key, cat]) => {
      const blocks = cat.blocks.filter(
        (b) =>
          b.name.toLowerCase().includes(term) ||
          b.keywords.some((k) => k.includes(term))
      );
      result[key] = { ...cat, blocks };
    });
    return result;
  }, [search, categories]);

  const toggleCategory = (key) => {
    setCategories((prev) => ({
      ...prev,
      [key]: { ...prev[key], expanded: !prev[key].expanded },
    }));
  };

  if (!isOpen) return null;

  return createPortal(
    /* Render above navigation, toolbars, and the full-screen overlay */
    <div className="fixed inset-0 z-[10050] flex items-start justify-center bg-black/40">
      <div
        ref={menuRef}
        className="add-block-menu bg-white rounded-lg w-full max-w-3xl mt-24 shadow-xl p-4"
      >
        <div className="flex items-center mb-4 gap-2">
          <input
            autoFocus
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search blocks..."
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto pr-1">
          {Object.entries(filtered).map(([key, cat]) => (
            <div key={key} className="mb-4">
              <button
                onClick={() => toggleCategory(key)}
                className="flex items-center justify-between w-full text-left mb-2"
              >
                <span className="font-semibold text-sm">{cat.icon} {cat.title}</span>
                <span className="text-sm">{cat.expanded ? '▲' : '▼'}</span>
              </button>
              {cat.expanded && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {cat.blocks.map((block) => (
                    <button
                      key={block.id}
                      className="block-card"
                      onClick={() => onSelect(block.id)}
                      title={block.description || block.name}
                    >
                      <div className="block-icon">{block.icon}</div>
                      <div className="block-label">{block.name}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BlockMenuOverlay;
